/** Small colour helpers for the per-story accent override. */

function channels(hex: string): [number, number, number] | null {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) return null;
  const value =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : match[1];
  return [0, 2, 4].map((i) => parseInt(value.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function luminance([r, g, b]: [number, number, number]): number {
  const linear = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

export function contrastRatio(a: string, b: string): number | null {
  const ca = channels(a);
  const cb = channels(b);
  if (!ca || !cb) return null;
  const la = luminance(ca);
  const lb = luminance(cb);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * The text colour to put ON an author-chosen accent: whichever of black and
 * white reads better. One of the two always clears 4.5:1 (the worst case, a
 * mid grey, gives 4.58 with PURE black; a softer near-black would dip under),
 * so an author can't pick an accent that makes their own buttons unreadable.
 */
export function readableOn(accent: string): string | null {
  const dark = '#000000';
  const light = '#ffffff';
  const onDark = contrastRatio(accent, dark);
  const onLight = contrastRatio(accent, light);
  if (onDark === null || onLight === null) return null;
  return onDark >= onLight ? dark : light;
}

/**
 * An author's accent, moved towards black or white until it can be read as
 * TEXT on `background` (4.5:1). The accent is a button colour first, where
 * `readableOn` looks after it, but it also labels things ("Reply") straight
 * on the screen, and a pale accent on a pale screen would vanish there.
 */
export function readableAs(accent: string, background: string): string | null {
  const from = channels(accent);
  const back = channels(background);
  if (!from || !back) return null;
  // Black reaches 4.5:1 on any background of luminance 0.175 or more, white
  // on any of 0.183 or less, so there is always an end that gets there. The
  // old 0.4 cut-off sent mid greys towards white, which cannot.
  const towards = luminance(back) >= 0.179 ? 0 : 255;
  for (let step = 0; step <= 20; step += 1) {
    const mix = step / 20;
    const mixed = from.map((c) => Math.round(c + (towards - c) * mix)) as [
      number,
      number,
      number,
    ];
    const hex = `#${mixed.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
    if ((contrastRatio(hex, background) ?? 0) >= 4.5) return hex;
  }
  return towards === 0 ? '#000000' : '#ffffff';
}

export function isHexColour(value: unknown): value is string {
  return typeof value === 'string' && channels(value) !== null;
}
